import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pin, PinOff, Trash2 } from "lucide-react";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import {
  listAnnouncementsAdmin,
  createAnnouncement,
  updateAnnouncement,
  deactivateAnnouncement,
  ANNOUNCEMENT_TYPES,
  ANNOUNCEMENT_SCOPES,
  UNIVERSAL_TYPES,
  TYPE_LABELS,
  type AnnouncementWriteType,
  type AnnouncementWriteScope,
} from "@/api/announcements";

/**
 * Editorial desk for announcements — admin-only writes, no moderation queue,
 * because nothing here is user-generated.
 *
 * The form encodes the affinity-vs-barrier rule the same way the server does,
 * instead of letting an author discover it as a 400: picking a universal type
 * (India news, US–India news, platform) disables the language-barrier field
 * and says why. Affinity is always available because affinity cannot exclude
 * anyone — it is a ranking boost wearing a state's name.
 */
export default function AdminAnnouncementsLab() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The editorial ledger — everything, unfiltered. Retired rows stay listed
  // (badged) and can be revived right here; barrier rows are always visible
  // to the desk regardless of what languages the signed-in admin declares.
  const { data, isLoading } = useQuery({
    queryKey: ["admin-announcements"],
    queryFn: () => listAnnouncementsAdmin({ limit: 50 }),
  });

  async function run(label: string, fn: () => Promise<unknown>) {
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(label);
      qc.invalidateQueries({ queryKey: ["admin-announcements"] });
      qc.invalidateQueries({ queryKey: ["announcements"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request failed");
    }
  }

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Announcements desk"
        subtitle="Create, pin, retire. Targeting follows the affinity-vs-barrier rule — the form won't let you break it."
        breadcrumbs={[
          { label: "Lab", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Announcements" },
        ]}
      />

      {notice && (
        <div className="mb-4 rounded-xl border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-800">
          {notice}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <CreateForm onSubmit={(input) => run("Announcement published.", () => createAnnouncement(input))} />

      <h2 className="mb-3 mt-8 text-[11px] font-extrabold uppercase tracking-[0.13em] text-gray-500">
        The ledger
      </h2>
      <p className="mb-3 text-xs text-gray-500">
        The full ledger — including retired, future-dated and language-gated
        rows readers can't see. Retiring hides a row from every reader list;
        revive it from here any time.
      </p>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="space-y-2">
        {data?.data.announcements.map((a) => (
          <div
            key={a.id}
            className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="truncate font-medium text-gray-900">{a.title}</span>
                {!a.isActive && <Badge variant="red">retired</Badge>}
                {a.isPinned && <Badge variant="purple">pinned</Badge>}
                {a.affinityState && <Badge variant="yellow">boosts {a.affinityState}</Badge>}
                {a.requiresLanguage && <Badge variant="green">{a.requiresLanguage} only</Badge>}
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                {TYPE_LABELS[a.type] ?? a.type} · {a.scope}
                {a.city ? ` · ${a.city}` : ""}
                {a.state ? `, ${a.state}` : ""}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                run(a.isPinned ? "Unpinned." : "Pinned.", () =>
                  updateAnnouncement(a.id, { isPinned: !a.isPinned }),
                )
              }
            >
              {a.isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            </Button>
            {a.isActive ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => run("Retired.", () => deactivateAnnouncement(a.id))}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => run("Revived.", () => updateAnnouncement(a.id, { isActive: true }))}
              >
                Revive
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── the create form ─────────────────────────────────────────── */

function CreateForm({
  onSubmit,
}: {
  onSubmit: (input: Parameters<typeof createAnnouncement>[0]) => void;
}) {
  const [type, setType] = useState<AnnouncementWriteType>("community_news");
  const [scope, setScope] = useState<AnnouncementWriteScope>("metro");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [affinityState, setAffinityState] = useState("");
  const [requiresLanguage, setRequiresLanguage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [publishedAt, setPublishedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [isPinned, setIsPinned] = useState(false);

  const universal = UNIVERSAL_TYPES.includes(type);
  const canSubmit = title.trim().length >= 3 && summary.trim().length >= 3;

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs text-gray-500">
          Type
          <select
            value={type}
            onChange={(e) => {
              const next = e.target.value as AnnouncementWriteType;
              setType(next);
              // Mirror the server's structural rule instead of surfacing it
              // as a 400 later: news for everyone carries no barrier.
              if (UNIVERSAL_TYPES.includes(next)) setRequiresLanguage("");
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
          >
            {ANNOUNCEMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t] ?? t}
              </option>
            ))}
          </select>
        </label>

        <label className="text-xs text-gray-500">
          Scope
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as AnnouncementWriteScope)}
            className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
          >
            {ANNOUNCEMENT_SCOPES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <label className="text-xs text-gray-500 sm:col-span-2">
          Title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-xs text-gray-500 sm:col-span-2">
          Summary
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            maxLength={500}
            rows={2}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-xs text-gray-500">
          City
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="San Jose"
            className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-xs text-gray-500">
          State (2-letter)
          <input
            value={state}
            onChange={(e) => setState(e.target.value.toUpperCase())}
            placeholder="CA"
            maxLength={2}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-xs text-gray-500">
          Affinity state <span className="text-gray-400">— boosts, never hides</span>
          <input
            value={affinityState}
            onChange={(e) => setAffinityState(e.target.value)}
            placeholder="Maharashtra"
            className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-xs text-gray-500">
          Language barrier{" "}
          <span className="text-gray-400">— the ONLY hard filter</span>
          <input
            value={requiresLanguage}
            onChange={(e) => setRequiresLanguage(e.target.value)}
            placeholder="Tamil"
            disabled={universal}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
          />
          {universal && (
            <span className="mt-1 block text-[11px] text-amber-700">
              {TYPE_LABELS[type]} reaches everyone by definition — the server
              refuses a language barrier on it, so this field is off.
            </span>
          )}
          {!universal && (
            <span className="mt-1 block text-[11px] text-gray-400">
              Set only when the thing itself can't be joined without the
              language. Never to target an audience.
            </span>
          )}
        </label>

        <label className="text-xs text-gray-500">
          Image URL <span className="text-gray-400">— the card art, 1200x800</span>
          <input
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://…"
            className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-xs text-gray-500">
          Video URL <span className="text-gray-400">— optional clip; image-only is the norm</span>
          <input
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://…"
            className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-xs text-gray-500">
          Publish at{" "}
          <span className="text-gray-400">
            — the scheduler; a future time IS the scheduled card. Blank = now
          </span>
          <input
            type="datetime-local"
            value={publishedAt}
            onChange={(e) => setPublishedAt(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-xs text-gray-500">
          Expires at <span className="text-gray-400">— rarely; pride does not rot</span>
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
          <input
            type="checkbox"
            checked={isPinned}
            onChange={(e) => setIsPinned(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          Pin above everything
        </label>
      </div>

      <div className="mt-4">
        <Button
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              type,
              scope,
              title: title.trim(),
              summary: summary.trim(),
              city: city.trim() || undefined,
              state: state.trim() || undefined,
              affinityState: affinityState.trim() || undefined,
              requiresLanguage: requiresLanguage.trim() || undefined,
              imageUrl: imageUrl.trim() || undefined,
              videoUrl: videoUrl.trim() || undefined,
              publishedAt: publishedAt ? new Date(publishedAt).toISOString() : undefined,
              expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
              isPinned,
            })
          }
        >
          Publish
        </Button>
      </div>
    </section>
  );
}
